import { isEqual, throttle } from 'lodash/fp';

import Behavior from 'dashboard/components/GraphCanvasV2/behaviors/behavior';
import EventBus from 'dashboard/components/GraphCanvasV2/event-bus';
import CanvasClickEvent from 'dashboard/components/GraphCanvasV2/events/canvas-click-event';
import CanvasMouseDownEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-down-event';
import CanvasMouseMoveEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-move-event';
import CanvasMouseUpEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-up-event';
import CanvasViewportChangeEvent from 'dashboard/components/GraphCanvasV2/events/canvas-viewport-change-event';
import GraphEvent from 'dashboard/components/GraphCanvasV2/events/graph-event';
import KeyDownEvent from 'dashboard/components/GraphCanvasV2/events/key-down-event';
import KeyUpEvent from 'dashboard/components/GraphCanvasV2/events/key-up-event';
import NodeMouseDownEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-down-event';
import NodeMouseUpEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-up-event';
import NodePositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/node-position-change';
import RendererPaintEvent from 'dashboard/components/GraphCanvasV2/events/renderer-paint-event';
import SelectionBoxMouseDownEvent from 'dashboard/components/GraphCanvasV2/events/selection-box-mouse-down-event';
import SelectionBoxMouseMoveEvent from 'dashboard/components/GraphCanvasV2/events/selection-box-mouse-move-event';
import SelectionBoxMouseUpEvent from 'dashboard/components/GraphCanvasV2/events/selection-box-mouse-up-event';
import SelectionBoxPositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/selection-box-position-change-event';
import { canvasSelectableFromG6Id } from 'dashboard/components/GraphCanvasV2/G6Mapper';
import G6Wrapper from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import {
  CanvasSelectable,
  collapsedGroupIdToSelectable,
  collapsedPairwiseNodeIdToSelectable,
  HashableCanvasSelectable,
  nodeIdToHashable,
  nodeIdToSelectable,
  RenderedCanvasNodeType,
  selectableFromHashable,
  selectableToHashable,
} from 'dashboard/models/graph';
import { GraphExplorer } from 'dashboard/models/graph-explorer';

import CollapsedGroupMouseDownEvent from '../events/collapsed-group-mouse-down-event';
import CollapsedGroupMouseUpEvent from '../events/collapsed-group-mouse-up-event';
import CollapsedGroupPositionChangeEvent from '../events/collapsed-group-position-change';
import CollapsedPairwiseNodeMouseDownEvent from '../events/collapsed-pairwise-node-mouse-down-event';
import CollapsedPairwiseNodeMouseUpEvent from '../events/collapsed-pairwise-node-mouse-up-event';
import CollapsedPairwiseNodePositionChangeEvent from '../events/collapsed-pairwise-node-position-change';

export const CANVAS_SELECTABLES_SELECTION_BEHAVIOR_CHANGE_BEHAVIOR_NAME =
  'canvasSelectablesSelectionBehaviorBoxUpdate';

export default class CanvasSelectablesSelectionBehavior extends Behavior {
  private multiMode = false;
  private isBrushingCanvas = false;
  private wasBrushingCanvas = false;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private brushOriginPoint: { x: number; y: number };
  private isDraggingSelection = false;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private dragSelectionOriginPoint: { x: number; y: number };
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private dragSelectionNodeOriginPoints: Record<HashableCanvasSelectable, { x: number; y: number }>;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private multiMouseDownSelection: CanvasSelectable = null;

  constructor(
    protected g6Wrapper: G6Wrapper,
    protected graphExplorer: GraphExplorer,
    protected eventBus: EventBus,
  ) {
    super(g6Wrapper, graphExplorer, eventBus);
    this.graphExplorer.onSelectablesChange(
      this.handleSelectablesChange,
      CANVAS_SELECTABLES_SELECTION_BEHAVIOR_CHANGE_BEHAVIOR_NAME,
    );
  }

  public get events() {
    return {
      [NodeMouseUpEvent.type]: 'handleNodeMouseUp',
      [NodeMouseDownEvent.type]: 'handleNodeMouseDown',
      [CollapsedGroupMouseUpEvent.type]: 'handleCollapsedGroupMouseUp',
      [CollapsedGroupMouseDownEvent.type]: 'handleCollapsedGroupMouseDown',
      [CollapsedPairwiseNodeMouseDownEvent.type]: 'handleCollapsedPairwiseNodeMouseDown',
      [CollapsedPairwiseNodeMouseUpEvent.type]: 'handleCollapsedPairwiseNodeMouseUp',
      [CanvasClickEvent.type]: 'handleCanvasClick',
      [CanvasMouseDownEvent.type]: 'handleCanvasMouseDown',
      [CanvasMouseUpEvent.type]: 'handleCanvasMouseUp',
      [CanvasMouseMoveEvent.type]: 'handleCanvasMouseMove',
      [CanvasViewportChangeEvent.type]: 'handleCanvasViewportChange',
      [KeyDownEvent.type]: 'handleKeyDown',
      [KeyUpEvent.type]: 'handleKeyUp',
      [SelectionBoxMouseDownEvent.type]: 'handleSelectionBoxMouseDown',
      [SelectionBoxMouseMoveEvent.type]: 'handleSelectionBoxMouseMove',
      [SelectionBoxMouseUpEvent.type]: 'handleSelectionBoxMouseUp',
      [RendererPaintEvent.type]: 'handleRendererPaint',
    };
  }

  public reactivate() {
    return;
  }

  public reset() {
    this.commitSelection();

    this.multiMode = false;
    this.isBrushingCanvas = false;
    this.wasBrushingCanvas = false;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.brushOriginPoint = null;
    this.isDraggingSelection = false;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragSelectionOriginPoint = null;
    this.dragSelectionNodeOriginPoints = {};
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.multiMouseDownSelection = null;
    this.graphExplorer.offSelectablesChange(
      CANVAS_SELECTABLES_SELECTION_BEHAVIOR_CHANGE_BEHAVIOR_NAME,
    );
    this.graphExplorer.onSelectablesChange(
      this.handleSelectablesChange,
      CANVAS_SELECTABLES_SELECTION_BEHAVIOR_CHANGE_BEHAVIOR_NAME,
    );
  }

  public handleCollapsedGroupMouseDown(e: CollapsedGroupMouseDownEvent) {
    const { graphTarget: collapsedGroup } = e;
    const selectable = collapsedGroupIdToSelectable(collapsedGroup.groupId);
    if (this.multiMode) {
      this.handleSelectableMouseDownMulti(selectable);
    } else {
      this.handleSelectableMouseDownSingle(selectable);
    }
  }

  public handleCollapsedGroupMouseUp(e: CollapsedGroupMouseUpEvent) {
    const { graphTarget: collapsedGroup } = e;
    const selectable = collapsedGroupIdToSelectable(collapsedGroup.groupId);
    if (this.multiMode) {
      this.handleSelectableMouseUpMulti(selectable);
    } else {
      this.handleSelectableMouseUpSingle(selectable);
    }
  }

  public handleCollapsedPairwiseNodeMouseDown(e: CollapsedPairwiseNodeMouseDownEvent) {
    const { graphTarget: collapsedNode } = e;
    const selectable = collapsedPairwiseNodeIdToSelectable(collapsedNode.id.value);
    if (this.multiMode) {
      this.handleSelectableMouseDownMulti(selectable);
    } else {
      this.handleSelectableMouseDownSingle(selectable);
    }
  }

  public handleCollapsedPairwiseNodeMouseUp(e: CollapsedPairwiseNodeMouseUpEvent) {
    const { graphTarget: collapsedNode } = e;
    const selectable = collapsedPairwiseNodeIdToSelectable(collapsedNode.id.value);
    if (this.multiMode) {
      this.handleSelectableMouseUpMulti(selectable);
    } else {
      this.handleSelectableMouseUpSingle(selectable);
    }
  }

  public handleNodeMouseDown(e: NodeMouseDownEvent) {
    const { graphTarget: node } = e;
    const selectable = nodeIdToSelectable(node.id);
    if (this.multiMode) {
      this.handleSelectableMouseDownMulti(selectable);
    } else {
      this.handleSelectableMouseDownSingle(selectable);
    }
  }

  public handleNodeMouseUp(e: NodeMouseUpEvent) {
    const { graphTarget: node } = e;
    const selectable = nodeIdToSelectable(node.id);
    if (this.multiMode) {
      this.handleSelectableMouseUpMulti(selectable);
    } else {
      this.handleSelectableMouseUpSingle(selectable);
    }
  }

  private handleSelectableMouseUpSingle(_selectable: CanvasSelectable) {
    // do nothing
  }

  private handleSelectableMouseDownSingle(selectable: CanvasSelectable) {
    if (this.graphExplorer.isSelected(selectable)) {
      return;
    }

    this.graphExplorer.setSelectables([selectable]);
  }

  private handleSelectableMouseDownMulti(selectable: CanvasSelectable) {
    if (this.graphExplorer.isSelected(selectable)) {
      return;
    }

    const selected = this.graphExplorer.getSelectables();
    this.multiMouseDownSelection = selectable;
    this.graphExplorer.setSelectables([...selected, selectable]);
  }

  private handleSelectableMouseUpMulti(selectable: CanvasSelectable) {
    // do not deselect if hovering on selectable we just selected
    if (isEqual(this.multiMouseDownSelection, selectable)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.multiMouseDownSelection = null;
      return;
    }

    this.graphExplorer.toggleSelectables([selectable], false);
  }

  public handleCanvasClick(_e: GraphEvent<MouseEvent>) {
    if (this.multiMode || this.wasBrushingCanvas) {
      return;
    }

    this.graphExplorer.setSelectables([]);
    this.g6Wrapper.selectionBoxDestroy();
  }

  public handleCanvasMouseDown(e: GraphEvent<MouseEvent>) {
    this.isBrushingCanvas = true;
    if (!this.g6Wrapper.isBrushActive) {
      this.brushOriginPoint = this.g6Wrapper.getCanvasByClient(e.raw.clientX, e.raw.clientY);

      this.g6Wrapper.brushStart({
        width: 0,
        height: 0,
        x: this.brushOriginPoint.x,
        y: this.brushOriginPoint.y,
      });
    }
  }

  public handleCanvasMouseMove(e: GraphEvent<MouseEvent>) {
    if (!this.isBrushingCanvas || !this.g6Wrapper.isBrushActive) {
      return;
    }

    const point = this.g6Wrapper.getCanvasByClient(e.raw.clientX, e.raw.clientY);

    this.g6Wrapper.brushUpdate({
      width: Math.abs(point.x - this.brushOriginPoint.x),
      height: Math.abs(point.y - this.brushOriginPoint.y),
      x: Math.min(point.x, this.brushOriginPoint.x),
      y: Math.min(point.y, this.brushOriginPoint.y),
    });
  }

  private commitSelection() {
    if (!this.isBrushingCanvas || !this.g6Wrapper.isBrushActive || !this.g6Wrapper.brushRect) {
      return;
    }

    const brushRectBBox = this.g6Wrapper.brushRect.getBBox();

    const topLeftWorldPoint = this.g6Wrapper.getPointByCanvas(
      brushRectBBox.minX,
      brushRectBBox.minY,
    );
    const bottomRightWorldPoint = this.g6Wrapper.getPointByCanvas(
      brushRectBBox.maxX,
      brushRectBBox.maxY,
    );
    const g6Nodes = this.g6Wrapper.getNodesInBox(
      topLeftWorldPoint.x,
      topLeftWorldPoint.y,
      bottomRightWorldPoint.x,
      bottomRightWorldPoint.y,
    );

    const toSelect = g6Nodes.map((g6Node) => canvasSelectableFromG6Id(g6Node.getID()));

    this.g6Wrapper.brushEnd();
    this.isBrushingCanvas = false;

    let nextSelectables: CanvasSelectable[];
    if (this.multiMode) {
      const alreadySelected = this.graphExplorer.getSelectables();
      nextSelectables = Array.from(
        new Set([...alreadySelected, ...toSelect].map(selectableToHashable)),
      ).map(selectableFromHashable);
    } else {
      nextSelectables = toSelect;
    }

    this.graphExplorer.setSelectables(nextSelectables);
    this.throttledUpdateEvent();

    // cancel out the upcoming click event if we were brushing the canvas
    if (toSelect.length > 0) {
      this.wasBrushingCanvas = true;
      setTimeout(() => {
        this.wasBrushingCanvas = false;
      }, 100);
    }
  }

  public handleCanvasMouseUp(_e: GraphEvent<MouseEvent>) {
    this.commitSelection();
  }

  public handleCanvasViewportChange(_e: CanvasViewportChangeEvent) {
    this.throttledSelectionBoxUpdate();
    this.throttledUpdateEvent();
  }

  public handleKeyDown(e: KeyDownEvent) {
    const { raw: rawEvent } = e;

    if (rawEvent.key === 'Shift') {
      this.multiMode = true;
    }
  }

  public handleKeyUp(e: KeyUpEvent) {
    const { raw: rawEvent } = e;

    if (rawEvent.key === 'Shift') {
      this.multiMode = false;
    }
  }

  public handleSelectionBoxMouseDown(e: SelectionBoxMouseDownEvent) {
    this.isDraggingSelection = true;
    const worldPoint = this.g6Wrapper.getPointByClient(e.raw.x, e.raw.y);
    this.dragSelectionOriginPoint = { x: worldPoint.x, y: worldPoint.y };

    const selectables = this.graphExplorer.getSelectables();
    this.dragSelectionNodeOriginPoints = {};
    for (const selectable of selectables) {
      const { centerX, centerY } = this.g6Wrapper.nodeBBoxBySelectable(selectable);
      this.dragSelectionNodeOriginPoints[selectableToHashable(selectable)] = {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        x: centerX,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        y: centerY,
      };
    }
  }

  public handleSelectionBoxMouseMove(e: SelectionBoxMouseMoveEvent) {
    if (!this.isDraggingSelection) {
      return;
    }

    const worldPoint = this.g6Wrapper.getPointByClient(e.raw.x, e.raw.y);
    const selectables = this.graphExplorer.getSelectables();

    const nodePositionChanges = selectables.reduce((memo, selectable) => {
      if (selectable.type === RenderedCanvasNodeType.Node) {
        const hashableSelectable = selectableToHashable(selectable);
        return {
          ...memo,
          [nodeIdToHashable(selectable.selectable)]: {
            x:
              worldPoint.x -
              this.dragSelectionOriginPoint.x +
              this.dragSelectionNodeOriginPoints[hashableSelectable].x,
            y:
              worldPoint.y -
              this.dragSelectionOriginPoint.y +
              this.dragSelectionNodeOriginPoints[hashableSelectable].y,
          },
        };
      } else {
        return memo;
      }
    }, {});
    this.eventBus.dispatchEvent(new NodePositionChangeEvent(null, nodePositionChanges));

    const collapsedGroupPositionChanges = selectables.reduce((memo, selectable) => {
      if (selectable.type === RenderedCanvasNodeType.CollapsedGroup) {
        const hashableSelectable = selectableToHashable(selectable);
        return {
          ...memo,
          [selectable.selectable]: {
            x:
              worldPoint.x -
              this.dragSelectionOriginPoint.x +
              this.dragSelectionNodeOriginPoints[hashableSelectable].x,
            y:
              worldPoint.y -
              this.dragSelectionOriginPoint.y +
              this.dragSelectionNodeOriginPoints[hashableSelectable].y,
          },
        };
      } else {
        return memo;
      }
    }, {});
    this.eventBus.dispatchEvent(
      new CollapsedGroupPositionChangeEvent(null, collapsedGroupPositionChanges),
    );

    const collapsedNodePositionChanges = selectables.reduce((memo, selectable) => {
      if (selectable.type === RenderedCanvasNodeType.CollapsedPairwiseNode) {
        const hashableSelectable = selectableToHashable(selectable);
        return {
          ...memo,
          [selectable.selectable]: {
            x:
              worldPoint.x -
              this.dragSelectionOriginPoint.x +
              this.dragSelectionNodeOriginPoints[hashableSelectable].x,
            y:
              worldPoint.y -
              this.dragSelectionOriginPoint.y +
              this.dragSelectionNodeOriginPoints[hashableSelectable].y,
          },
        };
      } else {
        return memo;
      }
    }, {});
    this.eventBus.dispatchEvent(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      new CollapsedPairwiseNodePositionChangeEvent(null, collapsedNodePositionChanges),
    );

    this.g6Wrapper.selectionBoxUpdateByCanvasSelectables(selectables);
    this.throttledUpdateEvent();
  }

  private throttledUpdateEvent = throttle(16, () => {
    if (this.g6Wrapper.selectionBox && !this.g6Wrapper.selectionBox.destroyed) {
      this.eventBus.dispatchEvent(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        new SelectionBoxPositionChangeEvent(null, this.g6Wrapper.selectionBox.getBBox()),
      );
    }
  });

  public handleSelectionBoxMouseUp(e: SelectionBoxMouseUpEvent) {
    if (this.g6Wrapper.selectionBox && !this.g6Wrapper.selectionBox.destroyed) {
      const event = new SelectionBoxPositionChangeEvent(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        null,
        this.g6Wrapper.selectionBox.getCanvasBBox(),
      );
      this.eventBus.dispatchEvent(event);
    }

    this.handleSelectionBoxMouseMove(e);

    this.isDraggingSelection = false;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragSelectionOriginPoint = null;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragSelectionNodeOriginPoints = null;
  }

  private handleSelectablesChange = (selectables: CanvasSelectable[]) => {
    this.g6Wrapper.selectionBoxUpdateByCanvasSelectables(selectables);
  };

  private throttledSelectionBoxUpdate = throttle(16, () => {
    const selectables = this.graphExplorer.getSelectables();
    return this.g6Wrapper.selectionBoxUpdateByCanvasSelectables(selectables);
  });

  public handleRendererPaint(_e: RendererPaintEvent) {
    const selectables = this.graphExplorer.getSelectables();
    if (selectables.length > 0) {
      this.g6Wrapper.selectionBoxUpdateByCanvasSelectables(selectables);
      this.eventBus.dispatchEvent(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        new SelectionBoxPositionChangeEvent(null, this.g6Wrapper.selectionBox.getBBox()),
      );
    }
  }
}
